import { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ExternalLinkIcon } from "@heroicons/react/solid";
import Slider from "./Slider";

export default function Modal({
	showModal,
	setShowModal,
	project: {
		title,
		subtitle,
		description,
		link,
		gitlink,
		skills,
		roles,
		highlights,
		images,
	},
}) {
	return (
		<Transition.Root show={showModal} as={Fragment}>
			<Dialog
				as='div'
				className='fixed z-10 inset-0 overflow-y-auto'
				onClose={() => setShowModal(false)}
			>
				<div className='flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0'>
					<Transition.Child
						as={Fragment}
						enter='ease-out duration-300'
						enterFrom='opacity-0'
						enterTo='opacity-100'
						leave='ease-in duration-200'
						leaveFrom='opacity-100'
						leaveTo='opacity-0'
					>
						<Dialog.Overlay className='fixed inset-0 bg-opacity-75 transition-opacity' />
					</Transition.Child>

					<span
						className='hidden sm:inline-block sm:align-middle sm:h-screen'
						aria-hidden='true'
					>
						&#8203;
					</span>
					<Transition.Child
						as={Fragment}
						enter='ease-out duration-300'
						enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
						enterTo='opacity-100 translate-y-0 sm:scale-100'
						leave='ease-in duration-200'
						leaveFrom='opacity-100 translate-y-0 sm:scale-100'
						leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
					>
						<div className='inline-block align-bottom border-gray-600 border-2 bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl sm:w-full'>
							<div className='bg-gray-800'>
								<div className='flex items-start justify-between p-5 border-b border-solid border-gray-900 rounded-t'>
									<div className='flex flex-col'>
										<p className='text-yellow-500 text-lg font-semibold'>
											{title}
										</p>
										<p className='text-gray-400 text-sm md:text-base font-semibold'>
											{subtitle}
										</p>
									</div>
									<button
										className='p-1 ml-auto border-2 border-gray-600 rounded-sm float-right my-auto text-xl leading-none font-semibold outline-none focus:outline-none'
										onClick={() => setShowModal(false)}
									>
										<span className='text-gray-300 h-6 w-6 block outline-none focus:outline-none'>
											×
										</span>
									</button>
								</div>
								<Slider images={[...images]} />
								<div
									className={`${
										gitlink && "space-x-2"
									} flex items-center py-3 mx-5 border-b-2 border-gray-500`}
								>
									<a
										className={`${!gitlink && "hidden"}`}
										href={gitlink}
										target='__blank'
									>
										<svg
											className='inline-flex w-5 h-5 text-gray-300 hover:text-gray-400'
											xmlns='http://www.w3.org/2000/svg'
											viewBox='0 0 24 24'
										>
											<path
												fill='currentColor'
												d='M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z'
											/>
										</svg>
									</a>
									<div
										className={`${
											(!gitlink || !link) && "hidden"
										} text-gray-400`}
									>
										{" | "}
									</div>
									<a
										className={`${!link && "hidden"}`}
										href={link}
										target='__blank'
									>
										<ExternalLinkIcon className='inline-flex w-5 h-5 text-gray-300 transition-all hover:text-blue-500' />
									</a>
								</div>
								<div className='relative py-1 px-5 flex-auto'>
									<p className='my-0 text-gray-300 text-sm md:text-md font-semibold'>
										Description
									</p>
									<p
										className='my-1 text-gray-400 text-sm md:text-md'
										dangerouslySetInnerHTML={{ __html: description }}
									/>
								</div>
								<div className='relative py-0 px-5 flex-auto'>
									<p className='mb-2 text-gray-300 text-sm md:text-md font-semibold'>
										Roles
									</p>
									<div className='my-1 flex flex-wrap text-gray-300 text-sm'>
										{[...roles].map((role, index) => (
											<p
												key={index + "__" + role}
												className='bg-gray-600 hover:bg-gray-700 cursor-pointer mb-2 mr-2 px-2 py-1 rounded-sm'
											>
												{role}
											</p>
										))}
									</div>
								</div>
								<div
									className={`relative py-1 px-5 flex-auto ${
										!highlights && "hidden"
									}`}
								>
									<p className='my-0 text-gray-300 text-sm md:text-md font-semibold'>
										Highlights
									</p>
									<p className='my-1 text-gray-400 text-sm md:text-md'>
										{highlights && highlights}
									</p>
								</div>
								<div className='relative py-1 px-5 flex-auto'>
									<p className='my-1 text-gray-400 text-sm md:text-md font-semibold'>
										Tech Stack
									</p>
									<div className='my-2 flex flex-wrap text-gray-300 text-sm'>
										{[...skills].map((skill, index) => (
											<p
												key={index + "__" + skill}
												className='bg-gray-600 hover:bg-gray-700 cursor-pointer mb-2 mr-2 px-2 py-1 rounded-sm'
											>
												{skill}
											</p>
										))}
									</div>
								</div>
							</div>
							<div className='bg-gray-800 px-4 py-3 sm:px-5 sm:flex sm:flex-row-reverse'>
								<button
									type='button'
									className='mt-3 w-full inline-flex justify-center rounded-sm border border-gray-300 shadow-sm px-4 py-1 bg-gray-200 text-base font-medium text-gray-600 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm'
									onClick={() => setShowModal(false)}
								>
									Close
								</button>
							</div>
						</div>
					</Transition.Child>
				</div>
			</Dialog>
		</Transition.Root>
	);
}
